import { fetchWithAuth, getApiBase } from '@/lib/api';

interface ApiResponse<T> {
  data: T;
  meta?: { version?: string };
}

/** The licences sold in the iOS app: one each, monthly or yearly (KAN-10). */
export type AppleLicenseType = 'PRO' | 'BUSINESS' | 'MAX';
export type AppleInterval = 'monthly' | 'yearly';
export type AppleSubscriptionState = 'active' | 'grace' | 'billing_retry' | 'expired' | 'revoked';

/** Why a new purchase may not start; also `details.reason` of APPLE_PURCHASE_NOT_AVAILABLE. */
export type ApplePurchaseBlockReason =
  | 'FLAG_OFF'
  | 'NOT_ADMIN'
  | 'TEAM_WORKSPACE'
  | 'STRIPE_CONTRACT'
  | 'PARTNER_MANAGED'
  | 'MANUAL_BILLING'
  | 'LOCKED_BY_SUPPORT'
  | 'ALREADY_SUBSCRIBED';

/** One App Store product. The IDs come from the server, the app never spells them itself. */
export interface IAppleProductInfo {
  productId: string;
  licenseType: AppleLicenseType;
  interval: AppleInterval;
}

/** The workspace's App Store subscription as the server last heard of it from Apple. */
export interface IAppleSubscriptionInfo {
  productId: string;
  licenseType: AppleLicenseType;
  interval: AppleInterval;
  state: AppleSubscriptionState;
  expiresAt: string;
  /** In Apple's grace period: until when the licence keeps working while Apple retries. */
  gracePeriodExpiresAt?: string | null;
  autoRenew: boolean;
  /** A downgrade or crossgrade Apple applies at the next renewal. */
  pendingProductId?: string | null;
  environment: 'Production' | 'Sandbox' | 'Xcode';
}

/**
 * What the App Store screen may offer. `products` always lists all six, the
 * screen needs them to show and manage a subscription; `eligible` says
 * whether a new purchase may start.
 */
export interface IAppleOffer {
  eligible: boolean;
  reason?: ApplePurchaseBlockReason | null;
  products: IAppleProductInfo[];
  subscription?: IAppleSubscriptionInfo | null;
}

/**
 * `conflict`: recorded but parked, because the workspace meanwhile pays another
 * way or has more than one person. No licence comes of it; support sorts it out.
 */
export type AppleTransactionOutcome =
  | 'granted'
  | 'updated'
  | 'unchanged'
  | 'conflict'
  | 'foreign'
  | 'unassigned'
  | 'rejected';

/**
 * The server's answer to a transaction. `finish` says the outcome is recorded
 * for good and the app may finish the transaction with StoreKit; false only
 * when the server could not decide yet and wants it again.
 */
export interface IAppleTransactionResult {
  outcome: AppleTransactionOutcome;
  finish: boolean;
  subscription?: IAppleSubscriptionInfo | null;
}

const BASE = '/api/v1/billing/apple';

export async function getAppleOffer(): Promise<IAppleOffer> {
  const res = await fetchWithAuth(`${getApiBase()}${BASE}/offer`);
  const body = (await res.json()) as ApiResponse<IAppleOffer>;
  return body.data;
}

/**
 * The token StoreKit stamps on the purchase, so the server can tell which
 * workspace bought it. `productId` is recorded for support only.
 */
export async function createApplePurchaseIntent(productId: string): Promise<string> {
  const res = await fetchWithAuth(`${getApiBase()}${BASE}/intents`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ productId }),
  });
  const body = (await res.json()) as ApiResponse<{ appAccountToken: string }>;
  return body.data.appAccountToken;
}

/** Hands StoreKit's signed transaction to the server, which checks it with Apple's keys. */
export async function postAppleTransaction(jws: string): Promise<IAppleTransactionResult> {
  const res = await fetchWithAuth(`${getApiBase()}${BASE}/transactions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ jws }),
  });
  const body = (await res.json()) as ApiResponse<IAppleTransactionResult>;
  return body.data;
}
